/**
 * The exact region adjacency of a trace, kept with the artwork.
 *
 * `buildContourGraph` knows precisely which regions touch and along how much
 * boundary, because it walked the boundary. Selection cannot ask it later: the
 * label map is gone once the trace becomes objects, and recovering it would mean
 * re-rasterising. So the answer is recorded at trace time.
 *
 * This is deliberately NOT how `growSelection` works, and does not replace it.
 * They answer different questions and the user wants both:
 *
 *   - *Which regions physically touch* — this file. Exact, no tolerance, no
 *     notion of a background. Two regions are neighbours iff they share
 *     boundary.
 *   - *Which shapes read as one figure* — `growSelection`. Bridges hairline
 *     gaps on purpose, suppresses canvas-scale shapes, and is a judgement call
 *     about perception rather than a fact about pixels.
 *
 * Staleness is handled by refusing to be clever. Twelve-plus commands rewrite
 * geometry through an arbitrary `(objects) => objects` closure, and duplication
 * regenerates object ids while copying scene extensions verbatim, so no
 * invalidation hook could be trusted to cover them. Instead the table carries a
 * fingerprint of the geometry it was built from and is ignored wholesale when it
 * no longer matches — never repaired, never partially trusted. It is not deleted
 * either, because an undo can restore the geometry it describes.
 */

import type { VectorLayer } from '../core/model';
import type { VectorObject } from './types';

export const TRACE_TOPOLOGY_KEY = 'maipo:trace-topology';

export interface SerializedTopology {
  readonly version: 1;
  /** Geometry this table describes; anything else and it is ignored. */
  readonly fingerprint: string;
  readonly ids: readonly string[];
  /** `[indexA, indexB, sharedBoundaryLength]`, each unordered pair once. */
  readonly pairs: readonly (readonly [number, number, number])[];
}

/**
 * Identifies the geometry, and nothing else.
 *
 * Covers membership, order, identity, transform and every anchor position —
 * everything that can move a boundary — and deliberately excludes fill, name and
 * visibility, so recolouring artwork does not throw away its topology.
 *
 * Anchors, not just the node count. Counting nodes is cheaper and it is blind to
 * exactly the edit most likely to invalidate adjacency: dragging one anchor
 * across the artwork keeps the count identical, so a count-based fingerprint
 * matched and the stale table would have been used rather than ignored. An edit
 * that produces no mismatch is never discarded, so "discard on mismatch" is only
 * as good as what the fingerprint can see.
 */
export function sceneFingerprint(objects: readonly VectorObject[]): string {
  let hash = 0x811c9dc5;
  const mix = (value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    hash ^= 0x5f;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  };
  mix(String(objects.length));
  for (const object of objects) {
    mix(object.id);
    mix(object.type);
    mix(object.transform.join(','));
    if (object.type === 'path') {
      mix(String(object.geometry.nodes.length));
      for (const node of object.geometry.nodes) {
        // Anchors only: handles move with them for every edit that changes which
        // regions touch, and hashing three points per node triples the cost of
        // the check for no additional coverage.
        mix(node.anchor.x.toString(36));
        mix(node.anchor.y.toString(36));
      }
    } else mix('0');
  }
  return hash.toString(16);
}

/** Packs adjacency for storage. `neighbours` is keyed by object id, both ways. */
export function serializeTopology(
  objects: readonly VectorObject[],
  neighbours: ReadonlyMap<string, ReadonlyMap<string, number>>,
): SerializedTopology {
  const ids = objects.map((object) => object.id);
  const position = new Map(ids.map((id, index) => [id, index]));
  const pairs: [number, number, number][] = [];
  for (const [from, row] of neighbours) {
    const a = position.get(from);
    if (a === undefined) continue;
    for (const [to, length] of row) {
      const b = position.get(to);
      // Each unordered pair once; the reader mirrors it.
      if (b === undefined || b <= a) continue;
      pairs.push([a, b, length]);
    }
  }
  return { version: 1, fingerprint: sceneFingerprint(objects), ids, pairs };
}

function isTopology(value: unknown): value is SerializedTopology {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<SerializedTopology>;
  return record.version === 1
    && typeof record.fingerprint === 'string'
    && Array.isArray(record.ids)
    && Array.isArray(record.pairs);
}

/**
 * Reads the table back, or null if the layer has none or the geometry moved.
 *
 * Ignoring a stale table is not a failure mode — selection falls back to the
 * geometric path, which needs nothing stored.
 */
export function readTraceTopology(layer: VectorLayer): Map<string, Map<string, number>> | null {
  const raw = layer.vector.extensions[TRACE_TOPOLOGY_KEY];
  if (!isTopology(raw)) return null;
  if (raw.fingerprint !== sceneFingerprint(layer.vector.objects)) return null;
  const neighbours = new Map<string, Map<string, number>>();
  const link = (from: string, to: string, length: number) => {
    const row = neighbours.get(from) ?? new Map<string, number>();
    row.set(to, length);
    neighbours.set(from, row);
  };
  for (const pair of raw.pairs) {
    if (!Array.isArray(pair) || pair.length !== 3) continue;
    const [a, b, length] = pair;
    const from = raw.ids[a];
    const to = raw.ids[b];
    if (typeof from !== 'string' || typeof to !== 'string' || typeof length !== 'number') continue;
    link(from, to, length);
    link(to, from, length);
  }
  return neighbours.size > 0 ? neighbours : null;
}
